import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { authFetch } from '../utils/authFetch';
import { Card } from '../components/ui';

const API_BASE = '/api';

function toItToday() {
  const d = new Date();
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

function SignatureModal({ title, initialDataUrl, onClose, onSave }) {
  const canvasRef = useRef(null);
  const isDrawingRef = useRef(false);

  const setupCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;

    canvas.width = Math.round(rect.width * dpr);
    canvas.height = Math.round(rect.height * dpr);

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#111827';
    ctx.lineWidth = 2;

    ctx.clearRect(0, 0, rect.width, rect.height);

    if (initialDataUrl) {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0, rect.width, rect.height);
      };
      img.src = initialDataUrl;
    }
  };

  useEffect(() => {
    // setup quando apre
    setTimeout(setupCanvas, 0);

    const onResize = () => setupCanvas();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDataUrl]);

  const getPoint = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const onPointerDown = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    e.preventDefault();
    canvas.setPointerCapture?.(e.pointerId);

    isDrawingRef.current = true;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const p = getPoint(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  };

  const onPointerMove = (e) => {
    if (!isDrawingRef.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    e.preventDefault();

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const p = getPoint(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
  };

  const onPointerUp = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    e.preventDefault();
    isDrawingRef.current = false;
    canvas.releasePointerCapture?.(e.pointerId);
  };

  const clear = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    ctx.clearRect(0, 0, rect.width, rect.height);
  };

  const save = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dataUrl = canvas.toDataURL('image/png');
    onSave(dataUrl);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-3">
      <Card className="w-full max-w-lg shadow-xl p-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800 text-xl leading-none">
            ×
          </button>
        </div>

        <p className="text-xs text-gray-500 mb-2">Firma con mouse o dito.</p>

        <div className="border rounded-xl bg-gray-50 overflow-hidden">
          <canvas
            ref={canvasRef}
            className="w-full h-[220px]"
            style={{ touchAction: 'none' }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onPointerLeave={onPointerUp}
          />
        </div>

        <div className="flex justify-end gap-2 mt-3">
          <button
            type="button"
            onClick={clear}
            className="px-3 py-2 rounded-lg text-xs bg-gray-100 text-gray-700 hover:bg-gray-200"
          >
            Cancella
          </button>
          <button
            type="button"
            onClick={save}
            className="px-3 py-2 rounded-lg text-xs bg-emerald-600 text-white hover:bg-emerald-700"
          >
            Salva
          </button>
        </div>
      </Card>
    </div>
  );
}

function DeleteConfirmModal({ open, internalCode, onCancel, onConfirm }) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-4">
      <Card className="w-full max-w-md shadow-xl p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Eliminare manutenzione?</h3>
            <p className="text-xs text-gray-600 mt-1">
              {internalCode ? (
                <>
                  Stai per rimuovere <b>{internalCode}</b> dalla sessione.
                </>
              ) : (
                <>Stai per rimuovere questa manutenzione dalla sessione.</>
              )}
              <br />
              Questa azione non può essere annullata.
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="text-gray-500 hover:text-gray-800 text-xl leading-none"
            aria-label="Chiudi"
          >
            ×
          </button>
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-2 rounded-lg text-xs bg-gray-100 text-gray-700 hover:bg-gray-200"
          >
            Annulla
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="px-3 py-2 rounded-lg text-xs bg-red-600 text-white hover:bg-red-700"
          >
            Elimina
          </button>
        </div>
      </Card>
    </div>
  );
}

export default function ManutenzioniMultiple() {
  const navigate = useNavigate();
  const location = useLocation();

  const [batchId, setBatchId] = useState(null);

  const [luogo, setLuogo] = useState('');
  const [data, setData] = useState(toItToday());
  const [allegatoAlVit, setAllegatoAlVit] = useState('');
  const [numMacchine, setNumMacchine] = useState(1);

  // rows: { workOrderId, internalCode, compiled }
  const [rows, setRows] = useState([]);
  const allDone = rows.length > 0 && rows.every((r) => r.compiled);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [batchClienteSignature, setBatchClienteSignature] = useState(null);
  const [sigModalOpen, setSigModalOpen] = useState(false);

  const [deleteModal, setDeleteModal] = useState({
    open: false,
    workOrderId: null,
    internalCode: '',
  });

  const canCreate = useMemo(() => {
    const n = Number(numMacchine);
    return luogo.trim() && data.trim() && Number.isFinite(n) && n >= 1 && n <= 200;
  }, [luogo, data, numMacchine]);

  const clearAllUI = () => {
    setRows([]);
    setBatchId(null);
    setBatchClienteSignature(null);
    setSuccess('');
    setError('');
  };

  const loadMyOpenBatch = async () => {
    setError('');
    setLoading(true);
    try {
      const res = await authFetch(`${API_BASE}/work/maintenance/batch/my-open`);
      const json = await res.json().catch(() => null);

      if (!res.ok) throw new Error(json?.error || 'Errore caricamento sessione.');

      if (!json?.batch) {
        clearAllUI();
        return;
      }

      const safeRows = Array.isArray(json.rows) ? json.rows : [];

      setBatchId(json.batch._id);
      setLuogo(json.batch.luogoIntervento || '');
      setData(json.batch.data || toItToday());
      setAllegatoAlVit(json.batch.allegatoAlVit || '');
      setRows(safeRows);

      const sig =
        json.batch?.clienteSignature ||
        (safeRows.length ? (safeRows[0].clienteSignature || null) : null);

      setBatchClienteSignature(sig);

      if (json?.batch && safeRows.length === 0) {
        setError(
          'Hai una sessione aperta ma senza manutenzioni (sessione corrotta/monca). Premi "Chiudi sessione" e poi ricrea la tabella.'
        );
      }
    } catch (e) {
      setError(e.message || 'Errore inatteso.');
    } finally {
      setLoading(false);
    }
  };

  const forceReload = async () => {
    setError('');
    setSuccess('');
    await loadMyOpenBatch();
  };

  useEffect(() => {
    loadMyOpenBatch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // se torno da Compila e ho completedWorkOrderId
  useEffect(() => {
    const completedId = location.state?.completedWorkOrderId;
    if (!completedId) return;

    setRows((prev) => prev.map((r) => (r.workOrderId === completedId ? { ...r, compiled: true } : r)));

    loadMyOpenBatch();

    navigate(location.pathname, { replace: true, state: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const createBatch = async () => {
    setError('');
    setSuccess('');

    if (!canCreate) {
      setError('Compila Luogo, Data e un numero macchine valido (1–200).');
      return;
    }

    setLoading(true);
    try {
      const res = await authFetch(`${API_BASE}/work/maintenance/batch/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          luogoIntervento: luogo,
          data,
          allegatoAlVit,
          numMacchine: Number(numMacchine),
        }),
      });

      const json = await res.json().catch(() => null);
      if (!res.ok) throw new Error(json?.error || 'Errore creazione sessione.');

      setBatchId(json.batch?._id || null);
      setRows(Array.isArray(json.rows) ? json.rows : []);
      setSuccess(`Tabella creata: ${json.rows?.length || 0} manutenzioni pronte da compilare.`);
    } catch (e) {
      setError(e.message || 'Errore inatteso.');
    } finally {
      setLoading(false);
    }
  };

  const closeSession = async () => {
    setError('');
    setSuccess('');

    if (rows.length > 0 && !allDone) {
      setError('Puoi chiudere la sessione solo quando tutte le manutenzioni sono compilate.');
      return;
    }

    setLoading(true);
    try {
      if (!batchId) {
        clearAllUI();
        setSuccess('Sessione chiusa.');
        return;
      }

      const res = await authFetch(`${API_BASE}/work/maintenance/batch/${batchId}/close`, {
        method: 'POST',
      });
      const json = await res.json().catch(() => null);
      if (!res.ok) throw new Error(json?.error || 'Errore chiusura sessione.');

      clearAllUI();
      setSuccess('Sessione chiusa. Le manutenzioni non compariranno più in questa pagina.');
    } catch (e) {
      setError(e.message || 'Errore inatteso.');
    } finally {
      setLoading(false);
    }
  };

  const deleteRow = async (workOrderId) => {
    setError('');
    setSuccess('');

    if (!batchId) {
      setRows((prev) => prev.filter((r) => r.workOrderId !== workOrderId));
      return;
    }

    setLoading(true);
    try {
      const res = await authFetch(`${API_BASE}/work/maintenance/batch/${batchId}/rows/${workOrderId}`, {
        method: 'DELETE',
      });
      const json = await res.json().catch(() => null);
      if (!res.ok) throw new Error(json?.error || 'Errore eliminazione manutenzione.');

      await loadMyOpenBatch();
      setSuccess('Manutenzione eliminata dalla sessione.');
    } catch (e) {
      setError(e.message || 'Errore inatteso.');
    } finally {
      setLoading(false);
    }
  };

  const openManutenzione = (row) => {
    setError('');
    setSuccess('');

    navigate('/manutenzioni-multiple/compila', {
      state: {
        workOrderId: row.workOrderId,
        internalCode: row.internalCode,
        prefill: {
          data,
          allegatoAlVit,
          note: '',
          luogoIntervento: luogo,
          clienteSignature: batchClienteSignature || null,
        },
        returnTo: '/manutenzioni-multiple',
      },
    });
  };

  const saveBatchSignature = async (dataUrl) => {
    if (!batchId) return;

    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const res = await authFetch(`${API_BASE}/work/maintenance/batch/${batchId}/signature`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clienteSignature: dataUrl }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok) throw new Error(json?.error || 'Errore salvataggio firma.');

      setBatchClienteSignature(dataUrl);
      setSuccess('Firma sessione salvata.');
      setSigModalOpen(false);
    } catch (e) {
      setError(e.message || 'Errore inatteso.');
    } finally {
      setLoading(false);
    }
  };

  const clearBatchSignature = async () => {
    if (!batchId) return;

    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const res = await authFetch(`${API_BASE}/work/maintenance/batch/${batchId}/signature`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clienteSignature: null }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok) throw new Error(json?.error || 'Errore cancellazione firma.');

      setBatchClienteSignature(null);
      setSuccess('Firma sessione cancellata.');
    } catch (e) {
      setError(e.message || 'Errore inatteso.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-3 md:px-6 flex justify-center">
      <div className="w-full max-w-6xl space-y-6">
        {/* ✅ MODAL ELIMINA (DENTRO RETURN) */}
        <DeleteConfirmModal
          open={deleteModal.open}
          internalCode={deleteModal.internalCode}
          onCancel={() => setDeleteModal({ open: false, workOrderId: null, internalCode: '' })}
          onConfirm={async () => {
            const id = deleteModal.workOrderId;
            setDeleteModal({ open: false, workOrderId: null, internalCode: '' });
            if (id) await deleteRow(id);
          }}
        />

        {/* Header */}
        <Card className="shadow-sm px-4 py-4 md:px-6 md:py-5 flex items-center justify-between">
          <div>
            <h1 className="text-xl md:text-2xl font-semibold text-gray-800 mb-1">Manutenzioni multiple</h1>
            <p className="text-xs md:text-sm text-gray-500">
              La sessione è persistente: se ricarichi la pagina, ritrovi la lista finché non fai “Chiudi sessione”.
            </p>
          </div>
          <button
            onClick={() => navigate('/report-interventi')}
            className="hidden md:inline-flex px-3 py-1.5 rounded-lg text-xs bg-gray-100 text-gray-700 hover:bg-gray-200"
          >
            ← Indietro
          </button>
        </Card>

        {/* Form dati generali */}
        <Card className="shadow-md p-5">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Luogo intervento *</label>
              <input
                value={luogo}
                onChange={(e) => setLuogo(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                placeholder="Es. Cliente X - Stabilimento ..."
                disabled={!!batchId}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Data (gg/mm/aaaa) *</label>
              <input
                value={data}
                onChange={(e) => setData(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                placeholder="gg/mm/aaaa"
                disabled={!!batchId}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Allegato al VIT (opzionale)</label>
              <input
                value={allegatoAlVit}
                onChange={(e) => setAllegatoAlVit(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                placeholder="Rif. VIT / codice / note"
                disabled={!!batchId}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Numero macchine *</label>
              <input
                type="number"
                min={1}
                max={200}
                value={numMacchine}
                onChange={(e) => setNumMacchine(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                disabled={!!batchId}
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-3 mt-4">
            <button
              type="button"
              disabled={loading || !canCreate || !!batchId}
              onClick={createBatch}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                loading || !canCreate || !!batchId
                  ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                  : 'bg-indigo-600 text-white hover:bg-indigo-700'
              }`}
              title={batchId ? 'Hai già una sessione aperta' : ''}
            >
              {loading ? 'Creazione...' : 'Crea tabella'}
            </button>

            <button
              type="button"
              onClick={forceReload}
              disabled={loading}
              className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200"
            >
              Ricarica sessione
            </button>

            <button
              type="button"
              disabled={!batchId || loading || (rows.length > 0 && !allDone)}
              onClick={closeSession}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                batchId && !loading && (rows.length === 0 || allDone)
                  ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                  : 'bg-gray-200 text-gray-500 cursor-not-allowed'
              }`}
            >
              Chiudi sessione
            </button>
          </div>

          {error && (
            <div className="mt-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
              {error}
            </div>
          )}
          {success && (
            <div className="mt-3 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg p-3">
              {success}
            </div>
          )}
        </Card>

        {/* Firma cliente sessione */}
        <Card className="shadow-md p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-sm font-semibold text-gray-800">Firma cliente (sessione)</h2>
              <p className="text-xs text-gray-500">
                Questa firma verrà riutilizzata automaticamente nelle manutenzioni della sessione.
              </p>
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setSigModalOpen(true)}
                className="px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-600 text-white hover:bg-blue-700"
                disabled={!batchId}
                title={!batchId ? 'Crea prima la sessione' : ''}
              >
                {batchClienteSignature ? 'Modifica firma' : 'Aggiungi firma'}
              </button>

              {batchClienteSignature && (
                <button
                  type="button"
                  onClick={clearBatchSignature}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-100 text-gray-700 hover:bg-gray-200"
                  disabled={!batchId || loading}
                >
                  Cancella
                </button>
              )}
            </div>
          </div>

          <div className="border rounded-lg bg-gray-50 h-28 flex items-center justify-center overflow-hidden">
            {batchClienteSignature ? (
              <img src={batchClienteSignature} alt="Firma cliente sessione" className="h-full object-contain" />
            ) : (
              <span className="text-xs text-gray-400">Nessuna firma</span>
            )}
          </div>

          {sigModalOpen && (
            <SignatureModal
              title="Firma cliente (sessione)"
              initialDataUrl={batchClienteSignature}
              onClose={() => setSigModalOpen(false)}
              onSave={saveBatchSignature}
            />
          )}
        </Card>

        {/* Tabella */}
        <Card className="shadow-md p-5">
          {rows.length === 0 ? (
            <div className="text-sm text-gray-600">
              Nessuna manutenzione in sessione. Inserisci i dati sopra e clicca “Crea tabella”.
            </div>
          ) : (
            <div className="overflow-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 border-b">
                    <th className="py-2 pr-4">Codice interno</th>
                    <th className="py-2 pr-4">Compilata</th>
                    <th className="py-2 pr-4">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.workOrderId} className="border-b last:border-b-0">
                      <td className="py-2 pr-4 font-medium text-gray-800">{r.internalCode || '-'}</td>
                      <td className="py-2 pr-4">
                        {r.compiled ? (
                          <span className="inline-flex px-2 py-1 rounded-md text-xs bg-emerald-100 text-emerald-800">
                            Sì
                          </span>
                        ) : (
                          <span className="inline-flex px-2 py-1 rounded-md text-xs bg-orange-100 text-orange-800">
                            No
                          </span>
                        )}
                      </td>
                      <td className="py-2 pr-4">
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => openManutenzione(r)}
                            className="px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-600 text-white hover:bg-blue-700"
                          >
                            {r.compiled ? 'Modifica' : 'Compila'}
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              setDeleteModal({
                                open: true,
                                workOrderId: r.workOrderId,
                                internalCode: r.internalCode || '',
                              })
                            }
                            className="px-3 py-1.5 rounded-lg text-xs font-medium bg-red-600 text-white hover:bg-red-700"
                          >
                            Elimina
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="mt-3 text-xs text-gray-500">
                “Compilata” è coerente col DB. Se aggiorni la pagina, la lista resta.
              </div>
            </div>
          )}
        </Card>

        <div className="md:hidden flex justify-center">
          <button
            onClick={() => navigate('/report-interventi')}
            className="mt-2 px-4 py-2 rounded-lg bg-gray-100 text-gray-700 text-xs font-medium hover:bg-gray-200"
          >
            ← Indietro
          </button>
        </div>
      </div>
    </div>
  );
}
